import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, CheckCircle, Clock, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import OnboardingDialog from '@/components/OnboardingDialog'
import { Card, CardContent } from '@/components/ui/card'
import apiClient from '@/lib/api/client'
import { tenantGetCertification as sdkGetCertification, getCertificationRejectionReasons as sdkGetRejectionReasons } from '@/lib/api/generated/sdk.gen'

type CertStatus = 'PENDING' | 'VERIFIED' | 'REJECTED' | 'NONE'

export default function OnboardingBanner() {
  const navigate = useNavigate()
  // Hooks must be declared unconditionally to keep order stable across renders
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data: certData, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['tenant', 'certification'],
    queryFn: async () => {
      const res: any = await sdkGetCertification({ client: apiClient, responseStyle: 'fields', throwOnError: false })
      const payload: any = (res as any)?.data ?? res
      return payload?.data ?? null
    },
  })

  const status: CertStatus = certData?.certificationStatus ?? 'NONE'

  // 当状态为 REJECTED 时，查询拒绝原因供弹窗展示
  const { data: rejectionReasons } = useQuery({
    queryKey: ['tenant', 'certification', 'rejectionReasons'],
    queryFn: async () => {
      const res: any = await sdkGetRejectionReasons({ client: apiClient, responseStyle: 'fields', throwOnError: false })
      const payload: any = (res as any)?.data ?? res
      return payload?.data ?? []
    },
    enabled: status === 'REJECTED',
  })

  if (isLoading) return null
  if (status === 'VERIFIED') return null

  const onStart = () => setDialogOpen(true)
  const onView = () => setDialogOpen(true)

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardContent className="py-4 px-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {status === 'PENDING' && <Clock className="h-5 w-5 text-blue-600" />}
          {status === 'REJECTED' && <AlertCircle className="h-5 w-5 text-red-600" />}
          {status === 'NONE' && <CheckCircle className="h-5 w-5 text-blue-600" />}
          <div className="text-sm">
            {status === 'NONE' && (
              <div>
                <div className="font-medium text-blue-900">完善企业认证，解锁完整功能</div>
                <div className="text-blue-700/80">提交企业信息与资质，预计1个工作日完成审核</div>
              </div>
            )}
            {status === 'PENDING' && (
              <div>
                <div className="font-medium text-blue-900">认证审核中</div>
                <div className="text-blue-700/80">我们正在审核您的企业认证，请耐心等待</div>
              </div>
            )}
            {status === 'REJECTED' && (
              <div>
                <div className="font-medium text-red-900">认证被驳回</div>
                <div className="text-red-700/80">请根据审核意见修改后重新提交</div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status === 'NONE' && (
            <Button onClick={onStart} className="min-w-[120px]">去认证</Button>
          )}
          {status === 'PENDING' && (
            <Button variant="outline" onClick={() => refetch()} disabled={isRefetching}>
              <RefreshCw className="h-4 w-4 mr-2" /> 刷新状态
            </Button>
          )}
          {status === 'REJECTED' && (
            <>
              <Button variant="outline" onClick={onView}>查看详情</Button>
              <Button onClick={onStart}>重新提交</Button>
            </>
          )}
        </div>
      </CardContent>
      <OnboardingDialog open={dialogOpen} onOpenChange={setDialogOpen} status={status} rejectionReasons={rejectionReasons as any[] | undefined} />
    </Card>
  )
}


